import React from "react";
import ServiceIncidenciaNatureza from "@/services/ServiceIncidenciaNatureza";
import ServiceTabelasIncidencia from "@/services/ServiceTabelasIncidencia";
import { buscarTodasPaginas } from "@/lib/paginacao";
import {
  CAMPOS_INCIDENCIA,
  type CampoIncidencia,
  type NaturezaCatalogo,
} from "@/pages/folha-pagamento/acao-em-massa/acaoEmMassa";

export interface OpcaoIncidencia {
  code: string;
  name: string;
}

export interface CatalogosAcaoEmMassa {
  naturezas: NaturezaCatalogo[];
  incidencias: Record<CampoIncidencia, OpcaoIncidencia[]>;
  carregando: boolean;
  /** Algum catálogo falhou; os selects afetados ficam sem opções. */
  erro: boolean;
}

const INCIDENCIAS_VAZIAS: Record<CampoIncidencia, OpcaoIncidencia[]> = {
  cp: [],
  irrf: [],
  fgts: [],
  cprp: [],
  pis: [],
};

/** Naturezas (tabela eSocial, com incidências recomendadas) e códigos de cada incidência. */
export function useCatalogosAcaoEmMassa(): CatalogosAcaoEmMassa {
  const [catalogos, setCatalogos] = React.useState<CatalogosAcaoEmMassa>({
    naturezas: [],
    incidencias: INCIDENCIAS_VAZIAS,
    carregando: true,
    erro: false,
  });

  React.useEffect(() => {
    let ignore = false;
    let erro = false;
    // Sinal próprio: um 500 aborta o sinal global do interceptor e as requisições canceladas
    // nunca resolvem, o que travaria o Promise.all (sem catálogos e sem aviso de erro).
    const { signal } = new AbortController();
    const semFalhar = <T>(promise: Promise<T[]>) =>
      promise.catch(() => {
        erro = true;
        return [] as T[];
      });

    Promise.all([
      semFalhar(
        buscarTodasPaginas<NaturezaCatalogo>((page, size) =>
          ServiceIncidenciaNatureza.search({}, { page, size, signal }),
        ),
      ),
      ...CAMPOS_INCIDENCIA.map(({ tabela }) =>
        semFalhar(
          buscarTodasPaginas<OpcaoIncidencia>((page, size) =>
            ServiceTabelasIncidencia.search(tabela, {}, { page, size, sort: "code", signal }),
          ),
        ),
      ),
    ]).then(([naturezas, ...incidencias]) => {
      if (ignore) return;
      setCatalogos({
        naturezas: naturezas as NaturezaCatalogo[],
        incidencias: Object.fromEntries(
          CAMPOS_INCIDENCIA.map(({ key }, index) => [key, incidencias[index]]),
        ) as Record<CampoIncidencia, OpcaoIncidencia[]>,
        carregando: false,
        erro,
      });
    });

    return () => {
      ignore = true;
    };
  }, []);

  return catalogos;
}
